import { useState } from 'react';

import { startOfDay } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import DateFilterItem from '@/modules/analytics/components/filters/DateFilter/DateFilterItem/DateFilterItem';
import { DateRangeCalendar } from '@/modules/analytics/components/filters/DateFilter/DateRangeCalendar';
import { useDateRangeDraft } from '@/modules/analytics/components/filters/DateFilter/useDateRangeDraft';
import {
  RELATIVE_PERIODS,
  RELATIVE_PERIOD_I18N_KEY,
  resolveRange,
} from '@/modules/analytics/constants/timeRange';
import type { DateRange } from '@/modules/analytics/dateSelection';
import type { Period } from '@/modules/analytics/types';
import { toLocalDate } from '@/modules/analytics/utils';
import { formatRangeLabel } from '@/modules/analytics/utils/formatRangeLabel';
import type { MetricRange, RelativePeriod } from '@/shared/types/reports';
import { formatDate } from '@/util/dateUtils';

const PRESETS: { period: RelativePeriod; item: Period }[] = RELATIVE_PERIODS.map((period) => ({
  period,
  item: { title_key: RELATIVE_PERIOD_I18N_KEY[period] },
}));

const toCalendarDates = (value: MetricRange | null): { startDate: string; endDate: string } => {
  if (!value) {
    return { startDate: '', endDate: '' };
  }
  const { start, end } = resolveRange(value).timeRange;
  return { startDate: formatDate(start), endDate: formatDate(end) };
};

type RangePickerProps = {
  id?: string;
  value: MetricRange | null;
  onChange: (next: MetricRange) => void;
  onBlur?: () => void;
  disabled?: boolean;
};

export const RangePicker = ({ id, value, onChange, onBlur, disabled }: RangePickerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { startDate, endDate } = toCalendarDates(value);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      onBlur?.();
    }
  };

  const handleRangeSelect = (range: DateRange) => {
    const start = toLocalDate(range.startDate);
    const end = toLocalDate(range.endDate);
    if (start && end) {
      onChange({ kind: 'custom', start: start.toISOString(), end: end.toISOString() });
    }
  };

  const { shown, isDrafting, handleSelect } = useDateRangeDraft(
    startDate,
    endDate,
    handleRangeSelect,
    () => handleOpenChange(false)
  );

  const selectedPeriod = value?.kind === 'relative' ? value.period : null;
  const today = startOfDay(new Date());

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline-neutral"
          size="sm"
          type="button"
          disabled={disabled}
          className="w-fit"
        >
          <CalendarIcon />
          {value ? formatRangeLabel(value, t) : t('analytics.reports_detail_select_placeholder')}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={16}
        className="inline-flex w-auto items-start gap-3 overflow-hidden p-0"
      >
        <div className="flex min-w-40 flex-col items-start gap-1 self-stretch border-r border-stroke px-3 py-4">
          {PRESETS.map(({ period, item }) => (
            <DateFilterItem
              key={period}
              item={item}
              selected={!isDrafting && period === selectedPeriod}
              onClick={() => {
                onChange({ kind: 'relative', period });
                handleOpenChange(false);
              }}
            />
          ))}
        </div>
        <DateRangeCalendar
          selected={shown}
          onSelect={handleSelect}
          isDayDisabled={(day) => day > today}
          endMonth={today}
          numberOfMonths={1}
        />
      </PopoverContent>
    </Popover>
  );
};
